import { useEffect, useMemo, useRef, useState } from "react";
import DeskCloseX from "@/components/common/DeskCloseX";
import { Layers, Check, ArrowDown, ArrowUp, PanelTop, DoorOpen, Crown, Plus, Trash2, ArrowRight } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import BuildingSketch, { GroupSquare } from "./BuildingSketch";
import BlockWizardStep2 from "./BlockWizardStep2";
import { BackButton } from "./CrmShell";
import BlockWizardStep3 from "./BlockWizardStep3";
import DeskConfirm from "./DeskConfirm";
import { ordinal , useCrmAdmin } from "./crmShared";
import { useRequiredFields, requiredStyle } from "@/lib/useRequiredFields";
import { FieldError } from "@/components/common/RequiredField";
import { DSelect } from "@/components/common/DeskSelect";

const STEPS = ["Structure", "Typologies & niveaux", "Lots"];
const LIMITS = { nb_sous_sols: 10, nb_entresols: 5, nb_etages: 50, nb_attiques: 5 };
const ord = ordinal;

/** Génération locale des niveaux (miroir de build_levels côté backend) pour un croquis instantané. */
export function buildLevels(f, groups) {
  const out = []; let o = 0;
  for (let i = f.nb_sous_sols; i >= 1; i--) out.push({ code: `S${i}`, label: `${ord(i)} sous-sol`, kind: "sous_sol", order: o++ });
  for (let i = 1; i <= f.nb_entresols; i++) out.push({ code: f.nb_entresols === 1 ? "ES" : `ES${i}`, label: f.nb_entresols === 1 ? "Entresol" : `${ord(i)} entresol`, kind: "entresol", order: o++ });
  out.push({ code: "RDC", label: "Rez-de-chaussée", kind: "rdc", order: o++ });
  for (let i = 1; i <= f.nb_etages; i++) {
    const gi = groups.findIndex((g) => Math.min(g.start, g.end) <= i && i <= Math.max(g.start, g.end));
    out.push({ code: `R${i}`, label: `${ord(i)} étage`, kind: "etage", num: i, order: o++, group_index: gi >= 0 ? gi + 1 : null });
  }
  for (let i = 1; i <= f.nb_attiques; i++) out.push({ code: `ATT${i}`, label: f.nb_attiques === 1 ? "Attique" : `${ord(i)} attique`, kind: "attique", order: o++ });
  return out;
}

export function StepRail({ current }) {
  return (
    <div className="flex items-center" style={{ padding: "12px 16px 0" }} data-testid="block-wizard-steps">
      {STEPS.map((label, i) => {
        const n = i + 1; const done = n < current; const active = n === current;
        return (
          <div key={label} className="flex items-center" style={{ flex: i < STEPS.length - 1 ? 1 : "0 0 auto" }}>
            <div className="flex items-center" style={{ gap: 7 }}>
              <span className="inline-flex items-center justify-center" style={{ width: 18, height: 18, borderRadius: 9, background: active ? "#7c5cd0" : done ? "#2d4a3a" : "#242429", fontSize: 9.5, color: active ? "#fff" : "#6e6e78" }}>
                {done ? <Check size={10} color="#7fd6ab" /> : n}
              </span>
              <span style={{ fontSize: 10.5, color: active ? "#e8e8ec" : done ? "#8b8b93" : "#6e6e78", whiteSpace: "nowrap" }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 0.5, background: "#26262a", margin: "0 10px" }} />}
          </div>
        );
      })}
    </div>
  );
}

function Counter({ Icon, color, label, value, onChange, max, testId }) {
  return (
    <div className="flex items-center" style={{ gap: 9, background: "#18181c", borderRadius: 7, padding: "7px 10px" }} data-testid={testId}>
      <Icon size={14} style={{ color }} />
      <span style={{ flex: 1, fontSize: 11.5, color: "#c9c9d1" }}>{label}</span>
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))} style={{ width: 16, fontSize: 14, color: "#8b8b93" }} aria-label="moins" data-testid={`${testId}-minus`}>−</button>
      <span className="text-center tabular-nums" style={{ width: 22, fontSize: 13, color: "#e8e8ec" }} data-testid={`${testId}-value`}>{value}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} style={{ width: 16, fontSize: 14, color: "#8b8b93" }} aria-label="plus" data-testid={`${testId}-plus`}>+</button>
    </div>
  );
}

const selStyle = { background: "#101013", border: "0.5px solid #2e2e34", borderRadius: 5, padding: "3px 8px", fontSize: 11, color: "#e8e8ec" };

/** Assistant bloc — création (étape 1) ou reprise d'un bloc existant (`initialBlock`, `initialStep`). */
export default function BlockWizard({ open, onClose, project, onCreated, initialBlock = null, initialStep = 1, focusLevel = null }) {
  const [name, setName] = useState("");
  const [f, setF] = useState({ nb_sous_sols: 0, nb_entresols: 0, nb_etages: 0, nb_attiques: 0 });
  const [groups, setGroups] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);
  const isAdmin = useCrmAdmin();
  const [s2ReadOnly, setS2ReadOnly] = useState(false); // iter156 P1 — étape 2 en lecture seule (bloc généré, hors édition)
  useEffect(() => { const prev = document.body.style.overflow; document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = prev; }; }, []); // iter156 P4
  const [block, setBlock] = useState(null);
  // iter205 (C4) — verrou de bloc dérivé : suivre le rafraîchissement du projet sans rouvrir l'assistant
  useEffect(() => { if (initialBlock) setBlock((b) => (b && b.id === initialBlock.id ? { ...b, locked: initialBlock.locked, levels: initialBlock.levels } : b)); }, [initialBlock]);
  const [orphanAsk, setOrphanAsk] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Brouillon serveur (repris depuis n'importe quel poste) : état de l'étape 2, sauvegarde anti-rebond 1 s.
  const [draft, setDraft] = useState(undefined);
  const [saveState, setSaveState] = useState({ status: "idle", at: null });
  const pending = useRef(null); const timer = useRef(null);
  const isEmptyDraft = (st) => !st || (!(st.sel || []).length && (st.cards || []).length <= 1 && !(st.cards || []).some((c) => c.lotDetails || Number(c.qty) > 1));
  const flush = async () => {
    if (!pending.current || !block) return;
    const { state, step: st } = pending.current; pending.current = null;
    setSaveState((x) => ({ ...x, status: "saving" }));
    try {
      if (isEmptyDraft(state)) await api.delete(`/crm/blocks/${block.id}/draft`, { data: { reason: "Brouillon vide après affectation" } });
      else await api.put(`/crm/blocks/${block.id}/draft`, { state, step: st });
      setSaveState({ status: "saved", at: Date.now() });
    }
    catch (e) {
      if (e?.response?.status === 403) { setSaveState({ status: "forbidden", at: null }); toast.error("Brouillon NON enregistré : droit « Gérer blocs & typologies » manquant — vos saisies seront perdues à la fermeture"); return; }
      setSaveState((x) => ({ ...x, status: "error" })); pending.current = { state, step: st }; timer.current = setTimeout(flush, 4000);
    }
  };
  const onDraftChange = (state) => { pending.current = { state, step: 2 }; clearTimeout(timer.current); timer.current = setTimeout(flush, 1000); };
  const saveAndClose = async () => { clearTimeout(timer.current); await flush(); onClose(); onCreated?.(block); };

  const draftFor = useRef(null); // id du bloc dont le brouillon est chargé — jamais rechargé (donc jamais démonté) tant que le bloc ne change pas
  useEffect(() => {
    if (!open) { draftFor.current = null; return; }
    if (!block?.id || draftFor.current === block.id) return;
    draftFor.current = block.id; setDraft(undefined);
    api.get(`/crm/blocks/${block.id}/draft`).then(({ data }) => setDraft(data?.state || null)).catch(() => setDraft(null));
  }, [open, block?.id]);
  const draftRef = useRef(null);
  const trackDraft = (state) => { draftRef.current = state; onDraftChange(state); };
  useEffect(() => {
    if (!open) return;
    setErr(""); setOrphanAsk(null);
    if (initialBlock) {
      setBlock(initialBlock); setStep(initialStep); setName(initialBlock.name);
      setF({ nb_sous_sols: initialBlock.nb_sous_sols || 0, nb_entresols: initialBlock.nb_entresols || 0, nb_etages: initialBlock.nb_etages || 0, nb_attiques: initialBlock.nb_attiques || 0 });
      setGroups((initialBlock.config_groups || []).map((g) => ({ start: g.start, end: g.end })));
    } else { setStep(1); setBlock(null); setName(""); setF({ nb_sous_sols: 0, nb_entresols: 0, nb_etages: 0, nb_attiques: 0 }); setGroups([]); }
  }, [open, initialBlock, initialStep]);
  const nameReq = useRequiredFields([{ key: "block-name", label: "Nom / Code du bloc", ok: !!name.trim() }]);
  const saveStructure = async (force = false) => {
    if (!nameReq.validate()) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/blocks/${block.id}`, { name: name.trim(), ...f, force, config_groups: groups.map((g) => ({ start: Math.min(g.start, g.end), end: Math.max(g.start, g.end) })) });
      setBlock(data); onCreated?.(data); setOrphanAsk(null); setRefreshKey((k) => k + 1); toast.success("Structure enregistrée"); setStep(2);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.orphans) setOrphanAsk(d); else setErr((typeof d === "string" && d) || d?.message || "Erreur");
    } finally { setBusy(false); }
  };
  const levels = useMemo(() => buildLevels(f, groups), [f, groups]);
  const floors = Array.from({ length: f.nb_etages }, (_, i) => i + 1);

  const setGroup = (i, k, v) => {
    const next = groups.map((g, j) => (j === i ? { ...g, [k]: Number(v) } : g));
    const g = next[i]; const a = Math.min(g.start, g.end), b = Math.max(g.start, g.end);
    const clash = next.find((o, j) => j !== i && a <= Math.max(o.start, o.end) && b >= Math.min(o.start, o.end));
    if (clash) { setErr(`Chevauchement : les étages ${Math.max(a, Math.min(clash.start, clash.end))} à ${Math.min(b, Math.max(clash.start, clash.end))} appartiennent déjà au groupe ${next.indexOf(clash) + 1}.`); return; }
    setErr(""); setGroups(next);
  };
  const addGroup = () => {
    const used = new Set(groups.flatMap((g) => floors.filter((n) => n >= Math.min(g.start, g.end) && n <= Math.max(g.start, g.end))));
    const free = floors.filter((n) => !used.has(n));
    if (!free.length) { setErr("Tous les étages courants appartiennent déjà à un groupe."); return; }
    let end = free[0]; while (free.includes(end + 1)) end++;
    setErr(""); setGroups([...groups, { start: free[0], end }]);
  };
  const setCount = (k) => (v) => { setF({ ...f, [k]: v }); if (k === "nb_etages") setGroups(groups.filter((g) => Math.max(g.start, g.end) <= v)); };

  const next = async () => {
    if (!nameReq.validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/crm/blocks", { project_id: project.id, name: name.trim(), ...f, config_groups: groups.map((g) => ({ start: Math.min(g.start, g.end), end: Math.max(g.start, g.end) })) });
      toast.success(`Bloc ${data.name} créé · ${data.levels.length} niveaux`);
      onCreated?.(data); setBlock(data); setStep(2);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" style={{ background: "#141418", left: "var(--sidebar-w)" }} data-desk-modal="" data-testid="block-wizard">
      {/* Iter127 — vrai plein écran : l'assistant remplit toute la zone de contenu à droite de la sidebar (aucun overlay, aucune marge). En-tête + pied fixes, seul le contenu défile. */}
      <div style={{ width: "100%", height: "100%", background: "#141418", fontFamily: "Tahoma, sans-serif", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div className="flex items-center" style={{ gap: 10, padding: "14px 16px 0" }}>
          <BackButton onBack={step === 3 && isAdmin ? () => setStep(2) : onClose} title={step === 3 && isAdmin ? "Étape précédente" : "Retour au projet"} />
          <span className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, background: "#241d38" }}><Layers size={16} style={{ color: "#a68ee0" }} /></span>
          <div style={{ flex: 1 }}><div style={{ fontSize: 14, color: "#e8e8ec" }}>{block ? `Bloc ${block.name}` : name.trim() ? `Nouveau bloc — ${name.trim()}` : "Nouveau bloc"}</div><div style={{ fontSize: 10, color: "#6e6e78" }}>Projet {project?.name}</div></div>
          <DeskCloseX onClick={onClose} testId="block-wizard-close" />
        </div>
        {step === 2 && block ? (
          draft !== undefined ? <BlockWizardStep2 block={block} projectName={project?.name} onBack={() => setStep(1)} onReadOnlyChange={setS2ReadOnly} onLotsChanged={() => onCreated?.(block)} onBlockChanged={(b) => { setBlock(b); onCreated?.(b); }} draft={draft} onDraftChange={trackDraft} onSaveClose={saveAndClose} saveState={saveState} /> : <div style={{ padding: 30, fontSize: 11, color: "#6e6e78", textAlign: "center" }}>Chargement du brouillon…</div>
        ) : step === 3 && block ? (
          <BlockWizardStep3 block={block} refreshKey={refreshKey} focusLevel={focusLevel} onBack={() => setStep(2)} onDone={() => { onCreated?.(block); onClose(); toast.success(`Bloc ${block.name} enregistré`); }}
            onLotChanged={() => onCreated?.(block)} />
        ) : (<>
        <div className="flex desk-overlay-scroll" style={{ gap: 16, padding: "14px 16px", overflowY: "auto", flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }} className="space-y-2">
            <div>
              <label style={{ fontSize: 10.5, color: "#8b8b93", display: "block", marginBottom: 4 }}>Nom / Code du bloc *</label>
              <input value={name} onChange={(e) => { setName(e.target.value); if (err && !err.startsWith("Chevauchement") && !err.startsWith("Tous les étages")) setErr(""); }} placeholder="A" autoFocus className="outline-none focus:border-[#7c5cd0]"
                style={{ background: "#18181c", border: "0.5px solid #2e2e34", borderRadius: 7, padding: "8px 11px", fontSize: 12.5, color: "#e8e8ec", maxWidth: 180, width: "100%", ...requiredStyle(nameReq.has("block-name")) }} data-field="block-name" data-testid="block-name" />
              <FieldError show={nameReq.has("block-name")} testId="block-name-error" />
            </div>
            <Counter Icon={ArrowDown} color="#7fa8dd" label="Sous-sols" value={f.nb_sous_sols} onChange={setCount("nb_sous_sols")} max={LIMITS.nb_sous_sols} testId="cnt-sous-sols" />
            <Counter Icon={PanelTop} color="#5fbf8f" label="Entresols" value={f.nb_entresols} onChange={setCount("nb_entresols")} max={LIMITS.nb_entresols} testId="cnt-entresols" />
            <div className="flex items-center" style={{ gap: 9, background: "#1c1730", border: "0.5px solid #2f2650", borderRadius: 7, padding: "7px 10px" }} data-testid="cnt-rdc">
              <DoorOpen size={15} style={{ color: "#a68ee0" }} /><span style={{ flex: 1, fontSize: 11.5, color: "#c4b3ea" }}>Rez-de-chaussée</span>
              <span style={{ fontSize: 9.5, color: "#7d6bab", background: "#141026", borderRadius: 4, padding: "3px 7px" }}>toujours</span>
            </div>
            <Counter Icon={ArrowUp} color="#e0a94a" label="Étages courants" value={f.nb_etages} onChange={setCount("nb_etages")} max={LIMITS.nb_etages} testId="cnt-etages" />
            <Counter Icon={Crown} color="#d9a7d9" label="Attiques" value={f.nb_attiques} onChange={setCount("nb_attiques")} max={LIMITS.nb_attiques} testId="cnt-attiques" />

            <div style={{ paddingTop: 8 }}>
              <div style={{ fontSize: 10.5, color: "#8b8b93", letterSpacing: ".04em" }}>GROUPES DE MÊME CONFIGURATION</div>
              <div style={{ fontSize: 10, color: "#6e6e78", lineHeight: 1.5, margin: "3px 0 8px" }}>Indiquez les étages identiques : vous ne saisirez leur contenu qu'une seule fois.</div>
              <div className="space-y-1.5">
                {groups.map((g, i) => (
                  <div key={i} className="flex items-center" style={{ gap: 6, background: "#18181c", borderRadius: 7, padding: "6px 9px", fontSize: 11, color: "#c9c9d1" }} data-testid={`group-row-${i + 1}`}>
                    <GroupSquare index={i + 1} />
                    <span>Du</span>
                    <DSelect value={g.start} onChange={(e) => setGroup(i, "start", e.target.value)} style={selStyle} data-testid={`group-start-${i + 1}`}>{floors.map((n) => <option key={n} value={n}>{ord(n)} étage</option>)}</DSelect>
                    <span>au</span>
                    <DSelect value={g.end} onChange={(e) => setGroup(i, "end", e.target.value)} style={selStyle} data-testid={`group-end-${i + 1}`}>{floors.map((n) => <option key={n} value={n}>{ord(n)} étage</option>)}</DSelect>
                    <span style={{ flex: 1 }} />
                    <button type="button" onClick={() => { setErr(""); setGroups(groups.filter((_, j) => j !== i)); }} style={{ color: "#d97a7a" }} aria-label="Supprimer le groupe" data-testid={`group-remove-${i + 1}`}><Trash2 size={13} /></button>
                  </div>
                ))}
                <button type="button" onClick={addGroup} disabled={!f.nb_etages} className="inline-flex items-center disabled:opacity-40" style={{ gap: 6, background: "#16241c", borderRadius: 7, padding: "8px 10px", fontSize: 11, color: "#7fd6ab" }} data-testid="group-add"><Plus size={14} />Ajouter un groupe</button>
              </div>
              {err && <div style={{ fontSize: 10.5, color: "#e79a9a", marginTop: 6 }} data-testid="block-wizard-error">{err}</div>}
            </div>
          </div>
          <BuildingSketch levels={levels} />
        </div>
        <div style={{ height: 0.5, background: "#222226" }} />
        <div className="flex items-center justify-between" style={{ padding: "12px 16px" }}>
          <span style={{ fontSize: 10.5, color: "#6e6e78" }}>Étape 1 sur 3</span>
          <div className="flex items-center" style={{ gap: 8 }}>
            <button type="button" onClick={onClose} style={{ background: "#18181c", border: "0.5px solid #2e2e34", borderRadius: 7, padding: "7px 14px", fontSize: 11.5, color: "#c9c9d1" }} data-testid="block-wizard-cancel">Annuler</button>
            <button type="button" onClick={block ? () => saveStructure(false) : next} disabled={busy} data-incomplete={!name.trim() || undefined} className="inline-flex items-center disabled:opacity-40" style={{ gap: 6, background: "#6d3fbf", borderRadius: 7, padding: "7px 14px", fontSize: 11.5, color: "#fff" }} data-testid="block-wizard-next">Suivant<ArrowRight size={13} /></button>
          </div>
        </div>
        </>)}
        <DeskConfirm open={!!orphanAsk} title="Des lots seraient impactés" confirmLabel="Archiver ces lots et continuer" busy={busy} onClose={() => setOrphanAsk(null)} onConfirm={() => saveStructure(true)} testId="block-structure-confirm"
          message={<>{orphanAsk?.message}<br /><br /><span style={{ color: "#eec173" }}>Les lots concernés seront archivés (restaurables depuis les archives du module Commercial), jamais supprimés.</span></>} />
      </div>
    </div>
  );
}
